import { POWER_STYLE, RAINBOW, ARMOR_SWATCH, GOLD_SWATCH, swatchAt } from "./palette";
import { paintBreakout } from "./render";
import type { Ball, BreakoutView, Brick, BrickKind, Paddle, PowerId } from "./types";

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const POWER_WEIGHTS: { id: PowerId; w: number }[] = [
  { id: "expand", w: 16 },
  { id: "shrink", w: 11 },
  { id: "multi", w: 13 },
  { id: "slow", w: 10 },
  { id: "haste", w: 9 },
  { id: "laser", w: 11 },
  { id: "pierce", w: 8 },
  { id: "life", w: 6 },
  { id: "sticky", w: 9 },
  { id: "shield", w: 9 },
  { id: "bomb", w: 7 },
];

const pickPower = (): PowerId => {
  const total = POWER_WEIGHTS.reduce((sum, item) => sum + item.w, 0);
  let roll = Math.random() * total;
  for (const item of POWER_WEIGHTS) {
    roll -= item.w;
    if (roll <= 0) return item.id;
  }
  return "expand";
};

const freshPaddle = (): Paddle => ({
  x: 0,
  y: 0,
  w: 90,
  h: 13,
  baseW: 90,
  scale: 1,
  flash: 0,
  tint: "#a78bfa",
});

/**
 * Login sol sahnesi için Arkanoid. İzleyici yokken raket topu kovalar;
 * imleç gelince kontrol oyuncuya geçer.
 */
export class BreakoutEngine implements BreakoutView {
  w = 0;
  h = 0;
  dark = false;
  reduced = false;
  score = 0;
  lives = 3;
  level = 1;
  combo = 0;
  heat = 0;
  veil = 0;
  veilWarm = false;
  shake = 0;
  controlled = false;
  paddle: Paddle = freshPaddle();
  balls: Ball[] = [];
  bricks: Brick[] = [];
  powers: BreakoutView["powers"] = [];
  particles: BreakoutView["particles"] = [];
  rings: BreakoutView["rings"] = [];
  floaters: BreakoutView["floaters"] = [];
  beams: BreakoutView["beams"] = [];
  shieldT = 0;
  laserT = 0;
  stickyT = 0;
  pierceT = 0;
  expandT = 0;
  shrinkT = 0;

  private pointerX: number | null = null;
  private phase: "play" | "clear" | "dead" = "play";
  private serveT = 0;
  private clearT = 0;
  private deadT = 0;
  private baseSpeed = 420;
  private comboClock = 0;
  private laserCool = 0;
  private slowT = 0;
  private hasteT = 0;
  private booted = false;

  setDark(dark: boolean) {
    this.dark = dark;
  }

  setReducedMotion(reduced: boolean) {
    this.reduced = reduced;
  }

  setPointer(x: number | null) {
    this.pointerX = x;
    this.controlled = x != null;
  }

  /** Yapışan topları bırakır. Oyun bittiyse sahneyi baştan kurar. */
  launch() {
    if (!this.booted) return;
    if (this.phase === "dead") {
      this.resetMatch();
      return;
    }
    if (this.phase !== "play") return;
    this.releaseStuck();
  }

  resize(nextW: number, nextH: number) {
    if (nextW < 32 || nextH < 32) {
      this.w = nextW;
      this.h = nextH;
      return;
    }

    if (!this.booted || this.w < 32) {
      this.w = nextW;
      this.h = nextH;
      this.baseSpeed = Math.min(nextW, nextH) * 0.56;
      this.layoutPaddle();
      this.booted = true;
      this.resetMatch();
      return;
    }

    const sx = nextW / this.w;
    const sy = nextH / this.h;
    this.w = nextW;
    this.h = nextH;
    this.baseSpeed = Math.min(nextW, nextH) * 0.56;
    if (Math.abs(sx - 1) > 0.02 || Math.abs(sy - 1) > 0.02) {
      for (const brick of this.bricks) {
        brick.x *= sx;
        brick.y *= sy;
        brick.w *= sx;
        brick.h *= sy;
      }
      for (const ball of this.balls) {
        ball.x *= sx;
        ball.y *= sy;
        for (const point of ball.trail) {
          point.x *= sx;
          point.y *= sy;
        }
      }
      for (const power of this.powers) {
        power.x *= sx;
        power.y *= sy;
      }
    }
    this.layoutPaddle();
  }

  step(dt: number) {
    if (!this.booted || this.w < 32 || this.h < 32) return;
    const stepDt = Math.min(0.034, Math.max(0, dt));
    this.decay(stepDt);
    this.updateBricks(stepDt);

    if (this.phase !== "dead") this.movePaddle(stepDt);

    if (this.phase === "play") {
      this.serveT += stepDt;
      const delay = this.stickyT > 0 ? 2.35 : 1.02;
      const anyFree = this.balls.some((ball) => ball.alive && !ball.stuck);
      const anyStuck = this.balls.some((ball) => ball.alive && ball.stuck);
      if (!anyFree && anyStuck && this.serveT > delay) this.releaseStuck();

      for (const ball of this.balls) this.simBall(ball, stepDt);
      this.updatePowers(stepDt);
      this.fireLaser(stepDt);

      if (!this.balls.some((ball) => ball.alive)) this.loseBall();
      else this.balls = this.balls.filter((ball) => ball.alive);
      this.checkClear();
    } else if (this.phase === "clear") {
      for (const ball of this.balls) this.simBall(ball, stepDt);
      this.clearT -= stepDt;
      if (this.clearT <= 0) this.nextLevel();
    } else {
      this.deadT -= stepDt;
      if (this.deadT <= 0) this.resetMatch();
    }

    this.updateFx(stepDt);
  }

  draw(ctx: CanvasRenderingContext2D, time: number) {
    paintBreakout(ctx, this, time);
  }

  private speed() {
    let mul = 1;
    if (this.slowT > 0) mul *= 0.64;
    if (this.hasteT > 0) mul *= 1.36;
    return this.baseSpeed * mul * (1 + this.heat);
  }

  private decay(dt: number) {
    const tick = (value: number) => Math.max(0, value - dt);
    this.expandT = tick(this.expandT);
    this.shrinkT = tick(this.shrinkT);
    this.slowT = tick(this.slowT);
    this.hasteT = tick(this.hasteT);
    this.laserT = tick(this.laserT);
    this.pierceT = tick(this.pierceT);
    this.stickyT = tick(this.stickyT);
    this.shieldT = tick(this.shieldT);
    this.heat = Math.max(0, this.heat - dt * 0.045);
    this.shake = this.reduced ? 0 : Math.max(0, this.shake - dt * 18);
    this.comboClock = Math.max(0, this.comboClock - dt);
    if (this.comboClock <= 0) this.combo = 0;
    this.veil = Math.max(0, this.veil - dt * 0.85);
    if (this.veil <= 0) this.veilWarm = false;
  }

  private layoutPaddle() {
    this.paddle.baseW = clamp(this.w * 0.2, 108, 280);
    this.paddle.h = 14;
    this.paddle.y = this.h - clamp(this.h * 0.09, 78, 116);
    this.paddle.w = this.paddle.baseW * this.paddle.scale;
    if (this.paddle.x === 0) this.paddle.x = this.w / 2;
    this.clampPaddle();
  }

  private clampPaddle() {
    const half = this.paddle.w / 2;
    this.paddle.x = clamp(this.paddle.x, half + 12, Math.max(half + 12, this.w - half - 12));
  }

  private movePaddle(dt: number) {
    let target = 1;
    if (this.expandT > 0) target *= 1.68;
    if (this.shrinkT > 0) target *= 0.56;
    target = clamp(target, 0.48, 1.95);
    this.paddle.scale += (target - this.paddle.scale) * Math.min(1, dt * 9);
    this.paddle.w = this.paddle.baseW * this.paddle.scale;
    this.paddle.flash = Math.max(0, this.paddle.flash - dt * 2.8);

    if (this.laserT > 0) this.paddle.tint = "#e879f9";
    else if (this.stickyT > 0) this.paddle.tint = "#c4b5fd";
    else if (this.shrinkT > 0) this.paddle.tint = "#fb7185";
    else if (this.expandT > 0) this.paddle.tint = "#22d3ee";
    else if (this.shieldT > 0) this.paddle.tint = "#2dd4bf";
    else this.paddle.tint = "#a78bfa";

    if (this.pointerX != null) {
      this.paddle.x += (this.pointerX - this.paddle.x) * Math.min(1, dt * 24);
    } else {
      const ball = this.threat();
      if (ball) {
        const look = ball.stuck ? ball.x : ball.x + ball.vx * 0.18;
        this.paddle.x += (look - this.paddle.x) * Math.min(1, dt * 3.6);
      }
    }
    this.clampPaddle();
  }

  private threat() {
    let best: Ball | null = null;
    for (const ball of this.balls) {
      if (!ball.alive) continue;
      if (!best || ball.y > best.y) best = ball;
    }
    return best;
  }

  private resetMatch() {
    this.level = 1;
    this.score = 0;
    this.lives = 3;
    this.combo = 0;
    this.comboClock = 0;
    this.phase = "play";
    this.veil = 0;
    this.veilWarm = false;
    this.powers = [];
    this.particles = [];
    this.rings = [];
    this.floaters = [];
    this.beams = [];
    this.clearTimers();
    this.paddle.scale = 1;
    this.buildLevel();
    this.spawnServe();
  }

  private nextLevel() {
    this.level += 1;
    this.phase = "play";
    this.powers = [];
    this.clearTimers();
    this.paddle.scale = 1;
    this.buildLevel();
    this.spawnServe();
  }

  private clearTimers() {
    this.expandT = 0;
    this.shrinkT = 0;
    this.slowT = 0;
    this.hasteT = 0;
    this.laserT = 0;
    this.pierceT = 0;
    this.stickyT = 0;
    this.shieldT = 0;
    this.heat = 0;
    this.laserCool = 0;
  }

  private spawnServe() {
    this.layoutPaddle();
    const r = clamp(this.w * 0.009, 6.5, 8.5);
    this.balls = [
      {
        x: this.paddle.x,
        y: this.paddle.y - r - 1.2,
        r,
        vx: 0,
        vy: 0,
        stuck: true,
        offset: 0,
        alive: true,
        squash: 0,
        trail: [],
      },
    ];
    this.serveT = 0;
  }

  private buildLevel() {
    const margin = 26;
    const gap = 7;
    const cols = clamp(Math.round((this.w - margin * 2) / 62), 7, 12);
    let rows = clamp(5 + Math.floor((this.level - 1) / 2), 5, 8);
    const bw = (this.w - margin * 2 - gap * (cols - 1)) / cols;
    const bh = clamp(this.h * 0.026, 14, 22);
    const top = Math.max(48, this.h * 0.068);
    while (rows > 4 && top + rows * (bh + gap) > this.h * 0.42) rows -= 1;

    const bricks: Brick[] = [];
    let golds = 0;
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        let kind = this.cellKind(col, row, cols, rows);
        if (!kind) continue;
        if (kind === "gold") {
          golds += 1;
          if (golds > 4) kind = "normal";
        }
        const maxHp = kind === "armored" ? 3 : kind === "solid" ? 2 : 1;
        const color =
          kind === "gold"
            ? GOLD_SWATCH
            : kind === "armored"
              ? ARMOR_SWATCH
              : (RAINBOW[(row + this.level) % RAINBOW.length] ?? 0);
        bricks.push({
          x: margin + col * (bw + gap),
          y: top + row * (bh + gap),
          w: bw,
          h: bh,
          hp: maxHp,
          maxHp,
          kind,
          color,
          alive: true,
          flash: 0,
          enter: this.reduced ? 1 : 0,
          wait: this.reduced ? 0 : row * 0.04 + col * 0.012,
          guard: 0,
          ox: 0,
          oy: 0,
          col,
          row,
        });
      }
    }
    this.bricks = bricks;
  }

  private cellKind(col: number, row: number, cols: number, rows: number): BrickKind | null {
    const pattern = (this.level - 1) % 5;
    const midX = (cols - 1) / 2;
    const midY = (rows - 1) / 2;

    if (pattern === 1 && (col + row) % 2 === 1) return null;
    if (pattern === 2) {
      const nx = Math.abs(col - midX) / (midX || 1);
      const ny = Math.abs(row - midY) / (midY || 1);
      if (nx + ny > 1.08) return null;
    }
    if (pattern === 3) {
      const dist = Math.abs(col - midX) / (midX || 1);
      if (dist > (row + 1) / rows) return null;
    }
    if (pattern === 4) {
      const nx = Math.abs(col - midX);
      const ny = Math.abs(row - midY);
      const edge = nx > midX - 1.4 || ny > midY - 1.05;
      const core = nx < 0.8 && ny < 0.8;
      if (!edge && !core) return null;
      if (core) return "gold";
    }

    if (col === Math.round(midX) && row === Math.round(midY)) return "gold";
    if ((col * 3 + row + this.level) % 13 === 0) return "gold";
    if (this.level >= 3 && Math.abs(col - midX) < 1.2 && row > 0 && row < rows - 1) {
      return "armored";
    }
    if ((this.level >= 2 && row === 0) || (this.level >= 5 && row < 2)) return "solid";
    return "normal";
  }

  private releaseStuck() {
    const sp = this.speed();
    let released = false;
    for (const ball of this.balls) {
      if (!ball.alive || !ball.stuck) continue;
      released = true;
      const jitter = (Math.random() - 0.5) * 0.58;
      ball.stuck = false;
      ball.vx = Math.sin(jitter) * sp;
      ball.vy = -Math.cos(jitter) * sp;
      ball.y = this.paddle.y - ball.r - 1.6;
    }
    if (released) this.serveT = 0;
  }

  private applySpeed(ball: Ball) {
    const sp = this.speed();
    let len = Math.hypot(ball.vx, ball.vy);
    if (len < 1) {
      ball.vx = 0;
      ball.vy = -sp;
      return;
    }
    if (Math.abs(ball.vy) < sp * 0.22) {
      ball.vy = Math.sign(ball.vy || -1) * sp * 0.22;
      len = Math.hypot(ball.vx, ball.vy);
    }
    ball.vx = (ball.vx / len) * sp;
    ball.vy = (ball.vy / len) * sp;
  }

  private simBall(ball: Ball, dt: number) {
    if (!ball.alive) return;
    ball.squash = Math.max(0, ball.squash - dt * 3.4);
    if (ball.stuck) {
      ball.x = this.paddle.x + ball.offset;
      ball.y = this.paddle.y - ball.r - 1.2;
      ball.vx = 0;
      ball.vy = 0;
      if (ball.trail.length > 4) ball.trail.shift();
      return;
    }

    this.applySpeed(ball);
    const dist = Math.hypot(ball.vx, ball.vy) * dt;
    const steps = Math.min(10, Math.max(1, Math.ceil(dist / 7)));
    const h = dt / steps;
    for (let i = 0; i < steps; i += 1) {
      if (!ball.alive || ball.stuck) break;
      ball.x += ball.vx * h;
      ball.y += ball.vy * h;
      this.collideWalls(ball);
      this.collidePaddle(ball);
      if (this.phase === "play") this.collideBricks(ball, h);
      this.collideShield(ball);
      if (ball.y - ball.r > this.h + 8) ball.alive = false;
    }

    if (ball.alive && !ball.stuck) {
      ball.trail.push({ x: ball.x, y: ball.y });
      if (ball.trail.length > 14) ball.trail.shift();
    }
  }

  private collideWalls(ball: Ball) {
    if (ball.x < ball.r) {
      ball.x = ball.r;
      ball.vx = Math.abs(ball.vx);
    } else if (ball.x > this.w - ball.r) {
      ball.x = this.w - ball.r;
      ball.vx = -Math.abs(ball.vx);
    }
    if (ball.y < ball.r) {
      ball.y = ball.r;
      ball.vy = Math.abs(ball.vy);
    }
  }

  private collidePaddle(ball: Ball) {
    if (ball.vy <= 0 || ball.stuck) return;
    const top = this.paddle.y;
    const left = this.paddle.x - this.paddle.w / 2;
    const right = left + this.paddle.w;
    if (ball.y < top - ball.r || ball.y > top + this.paddle.h) return;
    if (ball.x < left - ball.r || ball.x > right + ball.r) return;

    if (this.stickyT > 0) {
      ball.stuck = true;
      ball.offset = clamp(
        ball.x - this.paddle.x,
        -this.paddle.w / 2 + ball.r,
        this.paddle.w / 2 - ball.r,
      );
      ball.vx = 0;
      ball.vy = 0;
      ball.squash = 0.85;
      this.serveT = 0;
      this.paddle.flash = 1;
      return;
    }

    const rel = clamp((ball.x - this.paddle.x) / (this.paddle.w / 2), -1, 1);
    const angle = rel * 1.12;
    const sp = this.speed();
    ball.vx = Math.sin(angle) * sp;
    ball.vy = -Math.cos(angle) * sp;
    ball.y = top - ball.r - 0.8;
    ball.squash = 1;
    this.paddle.flash = Math.max(this.paddle.flash, 0.7);
    this.burst(ball.x, top, this.paddle.tint, 5, 80);
  }

  private collideShield(ball: Ball) {
    if (this.shieldT <= 0 || ball.vy <= 0 || ball.stuck) return;
    const y = this.paddle.y + this.paddle.h + 18;
    if (ball.y + ball.r < y || ball.y > y + 14) return;
    ball.y = y - ball.r - 0.6;
    ball.vy = -Math.abs(ball.vy) || -this.speed();
    ball.squash = 0.7;
    this.burst(ball.x, y, "#5eead4", 7, 100);
  }

  private hitsBrick(ball: Ball, brick: Brick) {
    const cx = clamp(ball.x, brick.x, brick.x + brick.w);
    const cy = clamp(ball.y, brick.y, brick.y + brick.h);
    const dx = ball.x - cx;
    const dy = ball.y - cy;
    return dx * dx + dy * dy <= ball.r * ball.r;
  }

  private collideBricks(ball: Ball, h: number) {
    if (!ball.alive || ball.stuck) return;
    const piercing = this.pierceT > 0;
    for (const brick of this.bricks) {
      if (!brick.alive || brick.enter < 0.92 || brick.guard > 0) continue;
      if (!this.hitsBrick(ball, brick)) continue;
      this.hurt(brick, ball.x, ball.y);
      if (!piercing) {
        this.bounceBrick(ball, brick, h);
        ball.squash = Math.max(ball.squash, 0.45);
        return;
      }
    }
  }

  private bounceBrick(ball: Ball, brick: Brick, h: number) {
    const prevX = ball.x - ball.vx * h;
    const prevY = ball.y - ball.vy * h;
    let flipX = prevX + ball.r <= brick.x + 0.8 || prevX - ball.r >= brick.x + brick.w - 0.8;
    let flipY = prevY + ball.r <= brick.y + 0.8 || prevY - ball.r >= brick.y + brick.h - 0.8;
    if (!flipX && !flipY) {
      const overlapX = Math.min(Math.abs(ball.x - brick.x), Math.abs(ball.x - (brick.x + brick.w)));
      const overlapY = Math.min(Math.abs(ball.y - brick.y), Math.abs(ball.y - (brick.y + brick.h)));
      if (overlapX < overlapY) flipX = true;
      else flipY = true;
    }
    if (flipX && flipY) {
      if (Math.abs(ball.vx) > Math.abs(ball.vy)) flipY = false;
      else flipX = false;
    }
    if (flipX) {
      ball.vx *= -1;
      ball.x =
        ball.x < brick.x + brick.w / 2
          ? brick.x - ball.r - 0.7
          : brick.x + brick.w + ball.r + 0.7;
    }
    if (flipY) {
      ball.vy *= -1;
      ball.y =
        ball.y < brick.y + brick.h / 2
          ? brick.y - ball.r - 0.7
          : brick.y + brick.h + ball.r + 0.7;
    }
  }

  private hurt(brick: Brick, x: number, y: number) {
    if (!brick.alive || brick.guard > 0) return;
    brick.hp -= 1;
    brick.flash = 1;
    brick.guard = 0.07;
    brick.ox = clamp((x - (brick.x + brick.w / 2)) * 0.15, -4, 4);
    brick.oy = clamp((y - (brick.y + brick.h / 2)) * 0.15, -3, 3);
    this.registerHit();
    const sw = swatchAt(brick.color);

    if (brick.hp > 0) {
      this.score += 5 * Math.max(1, this.combo);
      this.burst(x, y, sw.light, 6, 110);
      return;
    }

    brick.alive = false;
    const points = (brick.kind === "gold" ? 60 : 15 * brick.maxHp) * Math.max(1, this.combo);
    this.score += points;
    this.heat = Math.min(0.42, this.heat + (brick.kind === "gold" ? 0.06 : 0.035));
    this.popup(brick.x + brick.w / 2, brick.y, `+${points}`, this.combo >= 3 ? 15 : 12);
    this.burst(
      brick.x + brick.w / 2,
      brick.y + brick.h / 2,
      sw.glow,
      brick.kind === "gold" ? 18 : 12,
      170,
    );
    this.ring(
      brick.x + brick.w / 2,
      brick.y + brick.h / 2,
      sw.glow,
      brick.kind === "gold" ? 64 : 40,
    );
    if (!this.reduced) this.shake = Math.min(8, this.shake + (brick.kind === "armored" ? 2.6 : 1.3));
    this.maybeDrop(brick);
    this.checkClear();
  }

  private registerHit() {
    if (this.comboClock > 0) this.combo += 1;
    else this.combo = 1;
    this.comboClock = 1.25;
  }

  private maybeDrop(brick: Brick) {
    const falling = this.powers.filter((power) => power.alive).length;
    if (falling >= 5 && brick.kind !== "gold") return;
    if (brick.kind !== "gold" && Math.random() > 0.28) return;
    this.powers.push({
      id: pickPower(),
      x: brick.x + brick.w / 2,
      y: brick.y + brick.h / 2,
      vy: 72 + Math.random() * 50,
      phase: Math.random() * Math.PI * 2,
      r: 15,
      alive: true,
    });
  }

  private updatePowers(dt: number) {
    for (const power of this.powers) {
      if (!power.alive) continue;
      power.vy += 28 * dt;
      power.y += power.vy * dt;
      const half = this.paddle.w / 2 + power.r * 0.4;
      const caught =
        power.y + power.r >= this.paddle.y &&
        power.y <= this.paddle.y + this.paddle.h + 10 &&
        Math.abs(power.x - this.paddle.x) <= half;
      if (caught) {
        power.alive = false;
        this.collect(power.id);
        continue;
      }
      if (power.y > this.h + 24) power.alive = false;
    }
    this.powers = this.powers.filter((power) => power.alive);
  }

  private collect(id: PowerId) {
    this.paddle.flash = 1;
    const style = POWER_STYLE[id];
    this.burst(this.paddle.x, this.paddle.y, style.glow, 12, 150);
    this.ring(this.paddle.x, this.paddle.y, style.glow, 52);

    switch (id) {
      case "expand":
        this.expandT = 14;
        this.shrinkT = 0;
        break;
      case "shrink":
        this.shrinkT = 11;
        this.expandT = 0;
        break;
      case "multi":
        this.splitBalls();
        break;
      case "slow":
        this.slowT = 10;
        this.hasteT = 0;
        break;
      case "haste":
        this.hasteT = 9;
        this.slowT = 0;
        break;
      case "laser":
        this.laserT = 8.5;
        this.laserCool = 0;
        break;
      case "pierce":
        this.pierceT = 8;
        break;
      case "life":
        if (this.lives < 6) this.lives += 1;
        else {
          this.score += 150;
          this.popup(this.paddle.x, this.paddle.y - 28, "+150", 14);
        }
        break;
      case "sticky":
        this.stickyT = 12;
        break;
      case "shield":
        this.shieldT = 9;
        break;
      case "bomb":
        this.detonate();
        break;
    }
  }

  private splitBalls() {
    const sources = this.balls.filter((ball) => ball.alive).slice(0, 2);
    for (const source of sources) {
      this.cloneBall(source, 0.52);
      this.cloneBall(source, -0.52);
    }
  }

  private cloneBall(source: Ball, delta: number) {
    if (this.balls.filter((ball) => ball.alive).length >= 6) return;
    const sp = this.speed();
    const angle = (source.stuck ? -Math.PI / 2 : Math.atan2(source.vy, source.vx)) + delta;
    this.balls.push({
      x: source.x,
      y: source.y - (source.stuck ? source.r * 2 : 0),
      r: source.r,
      vx: Math.cos(angle) * sp,
      vy: Math.sin(angle) * sp,
      stuck: false,
      offset: 0,
      alive: true,
      squash: 0.7,
      trail: [],
    });
  }

  private detonate() {
    const x = this.paddle.x;
    const y = this.paddle.y;
    this.ring(x, y, "#fb7185", 200);
    this.burst(x, y, "#fda4af", 26, 280);
    if (!this.reduced) this.shake = Math.max(this.shake, 8);
    for (const brick of this.bricks) {
      if (!brick.alive) continue;
      const cx = brick.x + brick.w / 2;
      const cy = brick.y + brick.h / 2;
      if (Math.hypot(cx - x, cy - y) > 220) continue;
      brick.guard = 0;
      brick.hp = 1;
      this.hurt(brick, cx, cy);
    }
  }

  private fireLaser(dt: number) {
    if (this.laserT <= 0) return;
    this.laserCool -= dt;
    if (this.laserCool > 0) return;
    if (!this.balls.some((ball) => ball.alive && !ball.stuck)) return;
    this.laserCool = 0.22;
    const origins = [this.paddle.x - this.paddle.w * 0.3, this.paddle.x + this.paddle.w * 0.3];
    for (const x of origins) {
      let hit: Brick | null = null;
      for (const brick of this.bricks) {
        if (!brick.alive || brick.enter < 0.92) continue;
        if (x < brick.x || x > brick.x + brick.w) continue;
        if (brick.y + brick.h > this.paddle.y) continue;
        if (!hit || brick.y > hit.y) hit = brick;
      }
      const y1 = hit ? hit.y + hit.h : 16;
      this.beams.push({ x, y0: this.paddle.y, y1, life: 0.12, max: 0.12 });
      if (hit) this.hurt(hit, x, hit.y + hit.h);
    }
  }

  private loseBall() {
    if (this.phase !== "play") return;
    this.lives -= 1;
    this.veil = Math.max(this.veil, 0.55);
    this.veilWarm = true;
    if (!this.reduced) this.shake = Math.max(this.shake, 9);
    this.clearTimers();
    if (this.lives <= 0) {
      this.phase = "dead";
      this.deadT = 1.25;
      this.balls = [];
      return;
    }
    this.spawnServe();
  }

  private checkClear() {
    if (this.phase !== "play") return;
    if (this.bricks.some((brick) => brick.alive)) return;
    this.phase = "clear";
    this.clearT = 0.9;
    this.veil = Math.max(this.veil, 0.22);
    this.veilWarm = false;
    this.ring(this.w / 2, this.h * 0.24, "#fbbf24", 180);
    this.burst(this.w / 2, this.h * 0.24, "#fde68a", 30, 240);
  }

  private updateBricks(dt: number) {
    for (const brick of this.bricks) {
      if (brick.wait > 0) brick.wait = Math.max(0, brick.wait - dt);
      else if (brick.enter < 1) brick.enter = Math.min(1, brick.enter + dt * 2.6);
      brick.flash = Math.max(0, brick.flash - dt * 3.2);
      brick.guard = Math.max(0, brick.guard - dt);
      const drag = Math.max(0, 1 - dt * 9);
      brick.ox *= drag;
      brick.oy *= drag;
    }
  }

  private updateFx(dt: number) {
    for (const particle of this.particles) {
      particle.life -= dt;
      particle.vy += particle.gravity * dt;
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.vx *= Math.max(0, 1 - dt * 0.7);
      particle.rot += particle.spin * dt;
    }
    this.particles = this.particles.filter((particle) => particle.life > 0);

    for (const ring of this.rings) {
      ring.life -= dt;
      ring.r += ring.grow * dt;
    }
    this.rings = this.rings.filter((ring) => ring.life > 0);

    for (const floater of this.floaters) {
      floater.life -= dt;
      floater.y += floater.vy * dt;
    }
    this.floaters = this.floaters.filter((floater) => floater.life > 0);

    for (const beam of this.beams) beam.life -= dt;
    this.beams = this.beams.filter((beam) => beam.life > 0);
  }

  private burst(x: number, y: number, color: string, count: number, speed: number) {
    const n = this.reduced ? Math.max(2, Math.ceil(count / 3)) : count;
    for (let i = 0; i < n; i += 1) {
      if (this.particles.length > 220) return;
      const angle = (Math.PI * 2 * i) / n + Math.random() * 0.45;
      const sp = speed * (0.35 + Math.random() * 0.85);
      const life = 0.28 + Math.random() * 0.42;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * sp,
        vy: Math.sin(angle) * sp - 24,
        life,
        max: life,
        size: 1.4 + Math.random() * 2,
        color,
        kind: i % 4 === 0 ? "glow" : i % 3 === 0 ? "shard" : "spark",
        gravity: 340 + Math.random() * 200,
        rot: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 9,
      });
    }
  }

  private ring(x: number, y: number, color: string, radius: number) {
    if (this.rings.length > 16) this.rings.shift();
    this.rings.push({
      x,
      y,
      r: 8,
      grow: radius * 2.2,
      life: 0.4,
      max: 0.4,
      color,
      width: 2.4,
    });
  }

  private popup(x: number, y: number, text: string, size: number) {
    if (this.floaters.length > 14) this.floaters.shift();
    this.floaters.push({
      x,
      y,
      vy: -34,
      text,
      life: 0.7,
      max: 0.7,
      color: this.dark ? "#f8fafc" : "#1e1b4b",
      size,
    });
  }
}
